import { Injectable } from '@angular/core';
import { LoggerService } from '@core/services/logger/logger.service';
import { DentureDetailsQuery } from '@modules/denture-details/state/denture-details.query';
import { OrderQuery } from '@modules/order/state/order-query';
import { PatientQuery } from '@modules/patient/state/patient-query';
import { HostPlatformService } from '@shared/services/host-platform.service';
import { TeethDiagramQuery } from '@modules/teeth-diagram/state/teeth-diagram-query';
import { BridgeService } from '@shared/services/bridge.service';
import { ShellQuery } from '@shared/store/shell/shell-query';
import { ShellStore } from '@shared/store/shell/shell-store';
import { ReadOnlyRulesService } from '@shared/services/readOnlyRules.service';
import {
	CloseReason,
	ContextHeader,
	CustomRxEventType,
	RxCompleteData,
	RxStatusData,
	RxUIReadyData,
	TokenRefreshRequestData
} from '../../models/lab-broker-event.model';
import { LabBrokerEventBuilder } from '../../utils/lab-broker-event.builder';
import { LabBrokerCommunicationService } from '../../services/lab-broker-communication.service';
import { LabBrokerViewState } from '../../state/lab-broker.model';
import { LabBrokerQuery } from '../../state/lab-broker.query';
import { LabBrokerStore } from '../../state/lab-broker.store';
import { LAB_BROKER_ERROR_GENERIC, LAB_BROKER_TIMEOUTS, LAB_BROKER_TOKEN_FETCH_ERROR } from '@modules/lab-broker-view/consts';
import { CachedToken, CustomRxTokenService } from '../../services/custom-rx-token.service';
import { catchError, EMPTY, filter, map, Observable, switchMap, take, tap, throwError, timeout, combineLatest } from 'rxjs';
import { RxComponents } from '@shared/models/rx-models/interfaces/rx-components-model';
import { RxTemplateService } from '@shared/rx-templates/rx-template.service';
import { RxIntuitiveIndicationMapperService } from '@modules/rx-intuitive-teeth-orchestrator/services/rx-intuitive-indication-mapper.service';
import { BiLoggerService } from '@core/services/logger/bi-logger.service';
import {
	buildCustomRxDialogConfirmEvent,
	buildCustomRxUiCompletedEvent,
	buildCustomRxUiLoadedEvent,
	buildCustomRxUiNotRespondingEvent,
	CustomRxBiDialog,
	CustomRxBiDialogOption
} from '../../utils/custom-rx-bi-events';
import { DentiraCommunicationHandlers, DentiraCommunicationSession } from '@modules/lab-broker-view/services/dentira-communication-session';

@Injectable()
export class LabBrokerViewContainerFacade {
	readonly facadeName = 'LabBrokerViewContainerFacade';

	readonly isActive$ = this.query.isActive$;
	readonly viewState$ = this.query.viewState$;
	readonly isLoading$ = this.query.isLoading$;
	readonly isCompleted$ = this.query.isCompleted$;
	readonly isFooterVisible$ = this.query.isFooterVisible$;
	readonly isError$ = this.query.isError$;
	readonly isClosing$ = this.query.isClosing$;
	readonly isUnavailable$ = this.query.isUnavailable$;
	readonly errorMessage$ = this.query.errorMessage$;
	readonly iframeUrl$ = this.query.iframeUrl$;
	readonly integratorName$ = this.query.integratorName$;
	readonly selectedProcedure$ = this.orderQuery.procedure$;
	readonly sendTo$ = this.orderQuery.sendTo$;

	readonly isSaveAndContinueLaterButtonVisible$: Observable<boolean> = combineLatest([
		this.rxTemplateService.template$.pipe(map(template => template.ui.summaryFooter.saveAndContinueLaterButton.hidden !== true)),
		this.isCompleted$
	]).pipe(map(([isAvailableByTemplate, isCompleted]) => isAvailableByTemplate && !isCompleted));

	get isUnavailable(): boolean {
		return this.query.isUnavailable;
	}

	get isDialogSuppressed(): boolean {
		return this.query.isDialogSuppressed;
	}

	get integratorName(): string | null {
		return this.query.integratorName;
	}

	private lastHeaderReadonlyState?: Partial<RxComponents>;

	constructor(
		private readonly store: LabBrokerStore,
		private readonly query: LabBrokerQuery,
		private readonly communicationService: LabBrokerCommunicationService,
		private readonly shellQuery: ShellQuery,
		private readonly shellStore: ShellStore,
		private readonly patientQuery: PatientQuery,
		private readonly orderQuery: OrderQuery,
		private readonly logger: LoggerService,
		private readonly hostPlatformService: HostPlatformService,
		private readonly teethDiagramQuery: TeethDiagramQuery,
		private readonly bridgeService: BridgeService,
		private readonly dentureDetailsQuery: DentureDetailsQuery,
		private readonly tokenService: CustomRxTokenService,
		private readonly readOnlyRulesService: ReadOnlyRulesService,
		private readonly rxTemplateService: RxTemplateService,
		private readonly rxIntuitiveIndicationMapperService: RxIntuitiveIndicationMapperService,
		private readonly biLoggerService: BiLoggerService
	) {}

	trackDialogConfirm(dialog: CustomRxBiDialog, option: CustomRxBiDialogOption): void {
		void this.biLoggerService.reporter?.trackUserAction(buildCustomRxDialogConfirmEvent(dialog, option, this.partnerName));
	}

	setPatientHeaderReadonly() {
		this.lastHeaderReadonlyState ??= this.readOnlyRulesService.update({ patient: true });
	}

	resetPatientHeaderReadonly() {
		if (this.lastHeaderReadonlyState) {
			this.readOnlyRulesService.update(this.lastHeaderReadonlyState);
			this.lastHeaderReadonlyState = undefined;
		}
	}

	getToken(): Observable<CachedToken> {
		if (this.query.brokerConfig?.labCompanyId && this.shellQuery.companyId) {
			return this.tokenService
				.getToken(this.query.brokerConfig.labCompanyId, this.shellQuery.companyId)
				.pipe(timeout({ first: LAB_BROKER_TIMEOUTS.TokenFetchMs }));
		} else {
			return throwError(() => new Error('No labCompanyId in broker config or no companyId'));
		}
	}

	initCommunication(iframeNativeElement$: Observable<HTMLIFrameElement>, token$: Observable<CachedToken>) {
		return this.iframeUrl$.pipe(
			filter((url): url is string => url != null),
			take(1), // Wait for the first real url to set expected origin before subscribing
			tap(iframeUrl => this.communicationService.setExpectedOrigin(iframeUrl)),
			switchMap(iframeUrl => {
				const session = new DentiraCommunicationSession(
					this.communicationService.listenToEvents(),
					iframeNativeElement$,
					token$,
					this.buildSessionHandlers(iframeUrl),
					LAB_BROKER_TIMEOUTS.IframeReadyMs
				);

				return session.connect();
			})
		);
	}

	navigateAway(): void {
		this.sendRxExtensionClose('NAVIGATE_AWAY');
		this.teardownCommunication();
	}

	teardownCommunication(): void {
		// The store may already have been reset from outside (e.g. a failed save), so always release the iframe.
		this.communicationService.teardown();

		if (!this.query.isActive) {
			return;
		}

		this.store.reset();
		this.shellStore.update({ labBrokerSendStepBypassed: false });
	}

	suppressUnavailableDialog(): void {
		this.store.update({ dentiraUnavailableDialogSuppressed: true });
	}

	bypassLabBrokerSendStep(): void {
		this.shellStore.update({ labBrokerSendStepBypassed: true });
	}

	handleCloseRequests(): Observable<void> {
		return this.query.pendingCloseReason$.pipe(
			filter((reason): reason is CloseReason => reason !== null),
			tap(reason => this.runClose(reason)),
			map(() => undefined)
		);
	}

	private runClose(reason: CloseReason): void {
		this.logger.info('LabBrokerViewContainerFacade: Initiating close', {
			module: this.facadeName,
			extendedParameters: { reason }
		});

		this.store.update({ viewState: LabBrokerViewState.Closing });
		this.sendRxExtensionClose(reason);
	}

	private acknowledgeClose(trigger: string): void {
		if (!this.query.isClosePending) {
			return;
		}

		this.logger.info('LabBrokerViewContainerFacade: Close acknowledged, completing close', {
			module: this.facadeName,
			extendedParameters: { trigger }
		});
		this.store.update({ pendingCloseReason: null, viewState: LabBrokerViewState.Inactive });
	}

	private buildSessionHandlers(iframeUrl: string): DentiraCommunicationHandlers {
		return {
			onRxUIReady: (data, token, contentWindow) => {
				this.communicationService.setIframeWindow(contentWindow, iframeUrl);
				this.onRxUIReady(data, token);
			},
			onRxComplete: data => this.onRxComplete(data),
			onRxStatus: data => this.onRxStatus(data),
			onTokenRefreshRequest: data => this.handleTokenRefreshRequest(data),
			onTimeout: () => this.onIframeTimeout(),
			onError: err => this.onIframeError(err)
		};
	}

	private handleTokenRefreshRequest(data: TokenRefreshRequestData): Observable<void> {
		this.logger.info('LabBrokerViewContainerFacade: TokenRefreshRequest received', {
			module: this.facadeName,
			extendedParameters: { reason: data.Reason ?? '' }
		});

		this.store.update(state => ({
			lastReceivedEvent: {
				...state.lastReceivedEvent,
				[CustomRxEventType.TokenRefreshRequest]: { data, timestamp: new Date() }
			}
		}));

		return this.getToken().pipe(
			take(1),
			tap(token => this.sendTokenRefreshResponse(true, token)),
			catchError(err => {
				this.logger.warn('LabBrokerViewContainerFacade: Token refresh failed', {
					module: this.facadeName,
					error: err instanceof Error ? err : new Error(String(err))
				});
				this.sendTokenRefreshResponse(false, null, 'TOKEN_REFRESH_FAILED', 'Failed to refresh token');

				return EMPTY;
			}),
			map(() => undefined)
		);
	}

	private onRxUIReady(data: RxUIReadyData, token: CachedToken): void {
		this.store.update(state => ({
			lastReceivedEvent: {
				...state.lastReceivedEvent,
				[CustomRxEventType.RxUIReady]: { data, timestamp: new Date() }
			},
			viewState: LabBrokerViewState.Active
		}));

		void this.biLoggerService.reporter?.trackStage(buildCustomRxUiLoadedEvent(this.partnerName));

		this.sendRxInit(token);
	}

	private onRxComplete(data: RxCompleteData): void {
		if (!this.query.isActive) {
			return;
		}

		this.store.update(state => ({
			lastReceivedEvent: {
				...state.lastReceivedEvent,
				[CustomRxEventType.RxComplete]: { data, timestamp: new Date() }
			}
		}));

		if (data.Status === 'NOT_READY') {
			this.acknowledgeClose(`RxComplete ${data.Status}`);

			return;
		}

		if (data.Status === 'SUCCESS') {
			this.store.update({
				viewState: LabBrokerViewState.Completed,
				fullCustomRxPayload: data.FullCustomRx ?? null
			});

			void this.biLoggerService.reporter?.trackStage(buildCustomRxUiCompletedEvent(this.partnerName));
		} else {
			const errorMessage = data.Reason?.Message ?? LAB_BROKER_ERROR_GENERIC;

			this.store.update({
				viewState: LabBrokerViewState.Error,
				errorMessage
			});
		}

		this.acknowledgeClose(`RxComplete ${data.Status}`);
	}

	private onIframeError(error: unknown): void {
		this.logger.warn('LabBrokerViewContainerFacade: iframe error received', {
			module: this.facadeName,
			error: error instanceof Error ? error : new Error(String(error))
		});

		const isTokenError =
			(error as { isTokenFetchError?: boolean })?.isTokenFetchError === true ||
			(error instanceof Error && error.message.includes('rxappBffUrl'));

		this.store.update({
			viewState: LabBrokerViewState.Error,
			errorMessage: isTokenError ? LAB_BROKER_TOKEN_FETCH_ERROR : LAB_BROKER_ERROR_GENERIC
		});
	}

	private onIframeTimeout(): void {
		this.logger.warn('LabBrokerViewContainerFacade: iframe RxUIReady timeout', {
			module: this.facadeName
		});

		this.store.update({
			viewState: LabBrokerViewState.Unavailable,
			fullCustomRxPayload: null
		});

		void this.biLoggerService.reporter?.trackError(buildCustomRxUiNotRespondingEvent(this.partnerName));
	}

	private get partnerName(): string {
		return this.query.brokerConfig?.partnerName ?? '';
	}

	private sendTokenRefreshResponse(success: boolean, token: CachedToken | null, errorCode?: string, errorMessage?: string): void {
		const contextHeader = this.buildContextHeader();
		const event = LabBrokerEventBuilder.buildTokenRefreshResponseEvent({
			contextHeader,
			jwt: token?.accessToken,
			tokenType: token?.tokenType,
			success,
			errorCode,
			errorMessage
		});

		this.communicationService.sendEvent(event);

		this.logger.info('LabBrokerViewContainerFacade: TokenRefreshResponse sent', {
			module: this.facadeName,
			extendedParameters: { success: String(success) }
		});

		// Store the full event data for accurate debugging snapshots (issue 8)
		this.store.update(state => ({
			lastSentEvent: {
				...state.lastSentEvent,
				[CustomRxEventType.TokenRefreshResponse]: { data: event.Data, timestamp: new Date() }
			}
		}));
	}

	private onRxStatus(data: RxStatusData): void {
		this.logger.info('LabBrokerViewContainerFacade: RxStatus received', {
			module: this.facadeName,
			extendedParameters: { status: data.Status }
		});

		this.store.update(state => ({
			lastReceivedEvent: {
				...state.lastReceivedEvent,
				[CustomRxEventType.RxStatus]: { data, timestamp: new Date() }
			}
		}));

		if (data.Status === 'READY_TO_CLOSE') {
			this.acknowledgeClose('READY_TO_CLOSE');
		}

		if (data.Status === 'ERROR' && this.query.viewState === LabBrokerViewState.Active) {
			this.store.update({ viewState: LabBrokerViewState.Unavailable });
		}
	}

	private sendRxExtensionClose(reason: CloseReason): void {
		const contextHeader = this.buildContextHeader();
		const event = LabBrokerEventBuilder.buildRxExtensionCloseEvent({
			contextHeader,
			reason
		});

		this.communicationService.sendEvent(event);

		this.logger.info('LabBrokerViewContainerFacade: RxExtensionClose sent', {
			module: this.facadeName,
			extendedParameters: { reason }
		});

		this.store.update(state => ({
			lastSentEvent: {
				...state.lastSentEvent,
				[CustomRxEventType.RxExtensionClose]: { data: event.Data, timestamp: new Date() }
			}
		}));
	}

	private buildContextHeader(): ContextHeader {
		return LabBrokerEventBuilder.buildContextHeader({
			rxId: this.shellQuery.rxId,
			preparedNewRxId: this.shellQuery.preparedNewRxId,
			patientGuid: this.patientQuery.patientGuid,
			doctor: this.shellQuery.doctor,
			companyId: this.shellQuery.companyId,
			companyName: this.shellQuery.rx?.CompanyName ?? undefined,
			platform: this.hostPlatformService.isScanner ? 'SCANNER' : 'WEB'
		});
	}

	private sendRxInit(token: CachedToken): void {
		const { upperJaw, lowerJaw } = this.teethDiagramQuery.teeth;
		const teeth = this.teethDiagramQuery.getTreatedTeeth(upperJaw, lowerJaw);
		const bridges = this.bridgeService.getAllBridges({ upperJaw, lowerJaw });

		const data = LabBrokerEventBuilder.buildRxInitData({
			patient: this.patientQuery.patient,
			procedure: this.orderQuery.procedure,
			procedureType: this.orderQuery.type,
			userSettings: this.shellQuery.userSettings,
			languageCode: this.shellQuery.languageCode,
			teeth,
			bridges,
			dentureDetails: this.orderQuery.isDentureOrderV1 ? this.dentureDetailsQuery.getValue() : undefined,
			defaultUnitType: this.rxIntuitiveIndicationMapperService.getDefaultUnitType()
		});
		const contextHeader = this.buildContextHeader();
		const event = LabBrokerEventBuilder.buildRxInitEvent({ data, contextHeader, jwt: token.accessToken, tokenType: token.tokenType });

		this.communicationService.sendEvent(event);

		this.store.update(state => ({
			lastSentEvent: {
				...state.lastSentEvent,
				[CustomRxEventType.RxInit]: { data, timestamp: new Date() }
			}
		}));
	}
}
